import type { Product } from '@clockwise/clients/products';
import type { ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError, LifecycleStatus } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import {
  DetailList,
  type DetailListRow,
  DetailListSkeleton,
  Muted,
} from '@/components/custom/detail-list';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { ProductFormSheet } from '@/components/custom/product-form-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { ServiceRoleLabel, ServiceRoleList } from '@/components/custom/service-role-label';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, productsApi, serviceRolesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/activities/$id/')({
  validateSearch: detailSearch,
  component: ProductDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';

function ProductDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('activity.view', 'activities');
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: () => productsApi.get(id),
    enabled: canView,
  });

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'products',
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({
        entityType: 'products',
        entityId: id,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canView && canViewAudit,
  });

  const product = detail.data?.data ?? null;
  const [editOpen, setEditOpen] = useState(false);

  // Fetch the tenant's full service-role list so the detail view can
  // render every role this product supports by name. The list query is
  // cache-shared with the form/autocomplete (same queryKey shape).
  const SERVICE_ROLES_LIST_QUERY = {
    sortField: 'name',
    sortDirection: 'asc',
    limit: 200,
  } as const;
  const serviceRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
    enabled: canView,
  });
  warnIfTruncated(serviceRolesQuery.data, 'product-detail service-role names');
  const serviceRoles = serviceRolesQuery.data?.data ?? [];

  const statusMutation = useMutation({
    mutationFn: (target: LifecycleStatus) => productsApi.setStatus(id, { status: target as 0 | 1 }),
    onSuccess: (_, target) => {
      toast.success(target === LifecycleStatus.Active ? 'Reactivated' : 'Deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Status change failed';
      toast.error(msg);
    },
  });

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/activities">Back to activities</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : product ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{product.name}</h1>
                <StatusBadge status={product.status} />
              </div>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                {product.retainerCode ? (
                  <code className="rounded bg-muted px-1 py-0.5 text-xs" title="Retainer code">
                    {product.retainerCode}
                  </code>
                ) : null}
                {product.tmCode ? (
                  <code className="rounded bg-muted px-1 py-0.5 text-xs" title="T&M code">
                    {product.tmCode}
                  </code>
                ) : null}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RequirePermission permission="activity.edit">
            {product?.status === LifecycleStatus.Active ? (
              <Button
                variant="outline"
                disabled={statusMutation.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Deactivate "${product.name}"? Existing engagements continue to reference it.`,
                    )
                  ) {
                    statusMutation.mutate(LifecycleStatus.Inactive);
                  }
                }}
              >
                Deactivate
              </Button>
            ) : product?.status === LifecycleStatus.Inactive ? (
              <Button
                variant="outline"
                disabled={statusMutation.isPending}
                onClick={() => statusMutation.mutate(LifecycleStatus.Active)}
              >
                Reactivate
              </Button>
            ) : null}
            <Button disabled={!product} onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
          </RequirePermission>
        </div>
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="surface-card rounded-md border">
            {detail.isLoading ? (
              <DetailListSkeleton rows={DETAIL_ROW_COUNT} />
            ) : product ? (
              <DetailsTable product={product} serviceRoles={serviceRoles} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Activity not found.</p>
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <ProductFormSheet open={editOpen} onOpenChange={setEditOpen} product={product} />
    </PageShell>
  );
}

function DetailsTable({
  product,
  serviceRoles,
}: {
  product: Product;
  serviceRoles: readonly ServiceRole[];
}) {
  const roleById = new Map(serviceRoles.map((r) => [r.id, r]));
  const primary = roleById.get(product.serviceRoleId) ?? null;
  const extras = product.visibleToServiceRoleIds
    .map((id) => roleById.get(id))
    .filter((r): r is ServiceRole => r !== undefined);
  const rows: DetailListRow[] = [
    {
      label: 'Retainer code',
      value: product.retainerCode ? (
        <code className="font-mono text-sm">{product.retainerCode}</code>
      ) : (
        <Muted>—</Muted>
      ),
    },
    {
      label: 'T&M code',
      value: product.tmCode ? (
        <code className="font-mono text-sm">{product.tmCode}</code>
      ) : (
        <Muted>—</Muted>
      ),
    },
    { label: 'Name', value: product.name },
    { label: 'Description', value: product.description ?? <Muted>—</Muted> },
    {
      label: 'Service role (primary)',
      // truncate={false}: the detail grid's value cell is free to wrap, and a
      // clipped role name in a read-only view has nowhere else to surface.
      value: primary ? (
        <ServiceRoleLabel code={primary.shortCode || null} name={primary.name} truncate={false} />
      ) : (
        <Muted>—</Muted>
      ),
    },
    {
      label: 'Also visible to',
      value:
        extras.length > 0 ? (
          <ServiceRoleList roles={extras} />
        ) : (
          <Muted>— only the primary role</Muted>
        ),
    },
    { label: 'Kind', value: <KindBadge internal={product.internal} /> },
    {
      label: 'Budget',
      value: product.excludeFromBudget ? (
        <span className="text-sm">Excluded from consumption</span>
      ) : (
        <Muted>Counted normally</Muted>
      ),
    },
    { label: 'Status', value: <StatusBadge status={product.status} /> },
    {
      label: 'Created',
      value: new Date(product.createdAt).toLocaleString(),
    },
    {
      label: 'Updated',
      value: new Date(product.updatedAt).toLocaleString(),
    },
  ];
  return <DetailList rows={rows} />;
}

// Number of rows DetailsTable renders — keeps the loading skeleton the same
// height as the loaded card so the layout doesn't jump.
const DETAIL_ROW_COUNT = 11;

function KindBadge({ internal }: { internal: boolean }) {
  if (internal) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
        Internal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-900 dark:bg-sky-900/40 dark:text-sky-200">
      Customer
    </span>
  );
}

function StatusBadge({ status }: { status: LifecycleStatus }) {
  if (status === LifecycleStatus.Active) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  if (status === LifecycleStatus.Inactive) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        Inactive
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Archived
    </span>
  );
}
